/*!
 * WORK IN PROGRESS
 */

import { produce } from 'immer'
import { type PropsWithChildren, type ReactNode, useRef } from 'react'
import { type AriaLinkOptions, useLink } from 'react-aria'
import { useNavigate } from 'react-router'
import { tv, type VariantProps } from 'tailwind-variants'
import { buttonStyles } from './Button'

const linkStyles = tv({
  extend: buttonStyles,
  // TS won't allow this object unless to redefine all slots and variants.
  // Seems like a bug in tailwind-variants.
  slots: {
    root: 'rounded-none',
    label: '',
    startIcon: '',
    endIcon: '',
  },
  variants: {
    variant: {
      outlined: {},
      filled: {},
      ghost: {},
    },
  },
  compoundVariants: [
    {
      variant: ['outlined', 'filled'],
      className: {
        root: 'no-underline',
      },
    },
    {
      variant: ['ghost'],
      className: {
        root: 'inline underline-offset-4',
      },
    },
  ],
  compoundSlots: [
    {
      slots: ['startIcon', 'endIcon'],
      variant: ['outlined', 'filled'],
      className: 'h-full aspect-square',
    },
  ],
  defaultVariants: {
    variant: 'ghost',
  },
})

export interface UILinkProps
  extends
    Pick<AriaLinkOptions, 'target' | 'onPress'>,
    Pick<VariantProps<typeof linkStyles>, 'variant' | 'size' | 'color'>,
    StyleProps,
    PropsWithChildren {
  /**
   * The target URL
   *
   * String starts with `/` or `.` will be treated as local
   * navigation (client-side routing).
   */
  href?: string
  /**
   * The method to resolve a relative `href` (starts with `.`).
   * Same as react-router's `relative` option.
   *
   * Only works with local navigation.
   */
  relative?: 'route' | 'path'
  /**
   * Whether to replace the current entry in the history stack.
   * Same as react-router's `replace` option.
   *
   * Only works with local navigation.
   */
  replace?: boolean
  rel?: string
  disabled?: boolean
  icon?: ReactNode
  endIcon?: ReactNode
  title?: string

  startIconClassName?: string
  labelClassName?: string
  endIconClassName?: string

  onClick?: () => void
}

const transformLinkProps: (props: UILinkProps) => AriaLinkOptions = produce<
  AriaLinkOptions & UILinkProps
>(props => {
  props.isDisabled = props.disabled
  delete props.disabled
})

const RE_LOCAL_HREF = /^[/.]/

export function UILink(props: UILinkProps) {
  const ref = useRef<HTMLAnchorElement>(null)
  const linkOptions = transformLinkProps(props)
  const { linkProps } = useLink(linkOptions, ref)

  const navigate = useNavigate()
  // Enable client-side navigation when:
  // - `href` is a local path (starts with "/" or "."). And
  // - `target` is resolved to "_self" (which is the default).
  const clientSide =
    RE_LOCAL_HREF.test(props.href ?? '') &&
    (!props.target || props.target === '_self')

  const styles = linkStyles({
    variant: props.variant,
    color: props.color,
    size: props.size,
    hasLabel: Boolean(props.children),
    hasStartIcon: Boolean(props.icon),
    hasEndIcon: Boolean(props.endIcon),
  })

  return (
    <a
      {...linkProps}
      ref={ref}
      href={props.href}
      target={props.target}
      rel={props.rel}
      title={props.title}
      className={styles.root({ className: props.className })}
      style={props.style}
      onClick={ev => {
        props.onClick?.()
        if (clientSide) {
          ev.preventDefault()
          navigate(props.href!, {
            relative: props.relative,
            replace: props.replace,
          })
        }
      }}
    >
      <span
        className={styles.startIcon({ className: props.startIconClassName })}
      >
        {props.icon}
      </span>
      <span className={styles.label({ className: props.labelClassName })}>
        {props.children}
      </span>
      <span className={styles.endIcon({ className: props.endIconClassName })}>
        {props.endIcon}
      </span>
    </a>
  )
}
